/**
 * planPricing — admin-side reads/writes for the PLUS plan price catalog
 * (Settings → Plan Pricing). The office raises prices here; the PlanBuilder
 * reads the same pegasus_plan_price rows live, so a change here is the next
 * quote's number. Separate from PlanBuilder's usePlanCatalog because the admin
 * must see ARCHIVED rows too (active = false), not just the sellable ones.
 *
 * Repair discount is deliberately NOT edited here — it lives once in
 * Pricing → Membership Repair Discount (plan_discount tiers), and the builder
 * derives the per-term benefit from those same tiers (repairPctForTerm). One
 * source of truth, per "unify, don't pile on."
 */
import { type PlanOfferCover } from "@/lib/planCovers";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { PlanPrice } from "./PlanBuilder";
import type { TuneupTemplateItem } from "./obligations";
import type { PlanDiscount } from "@/features/customer-360/serviceTypes";
import { saveInvoiceRpc } from "@/features/invoice/officeInvoice";

export type PlanKind = "base" | "addon" | "item";

/** All rows incl. archived, for the admin. Ordered kind → sort. */
export function usePlanPriceCatalog() {
  return useQuery({
    queryKey: ["plan_price_admin"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<PlanPrice[]> => {
      const { data, error } = await supabase
        .from("pegasus_plan_price")
        .select("id, kind, name, sort, p1, p2, p3, p5, p10, flat, active, tuneups")
        .order("kind")
        .order("sort");
      if (error) throw error;
      // PostgREST returns numeric as string — coerce to number|null once here.
      return (data ?? []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        kind: r.kind as PlanKind,
        name: r.name as string,
        sort: Number(r.sort ?? 0),
        p1: r.p1 == null ? null : Number(r.p1),
        p2: r.p2 == null ? null : Number(r.p2),
        p3: r.p3 == null ? null : Number(r.p3),
        p5: r.p5 == null ? null : Number(r.p5),
        p10: r.p10 == null ? null : Number(r.p10),
        flat: r.flat == null ? null : Number(r.flat),
        active: r.active as boolean,
        tuneups: (r.tuneups as TuneupTemplateItem[] | null) ?? null,
      }));
    },
  });
}

/** MEMBERSHIPS-02 P1 — the purchase → term rule, answered by the database:
 *  renewals chain from the current plan's expiry (before expiry, or inside the
 *  grace window — 30 days, a setting); otherwise the term starts on the sale
 *  date. `reason` is the sentence the office sees under the start date. */
export interface RenewalStart {
  start_date: string;
  chains_from: string | null;
  prior_expire: string | null;
  prior_source: "esc" | "pegasus" | null;
  reason: string;
}
export async function planRenewalStart(custno: string, saleDate?: string): Promise<RenewalStart> {
  const { data, error } = await supabase.rpc("pegasus_plan_renewal_start", { p_custno: custno, p_sale_date: saleDate ?? new Date().toISOString().slice(0, 10) });
  if (error) throw error;
  const row = ((data ?? []) as RenewalStart[])[0];
  return row ?? { start_date: saleDate ?? new Date().toISOString().slice(0, 10), chains_from: null, prior_expire: null, prior_source: null, reason: "new member — the term starts the day of the sale" };
}
/** MEMBERSHIPS-03 rule 4a: the checks an EARLIER term still owes this
 *  customer that a renewal would fold (target 45+ days before the new start,
 *  not booked). Shown at the sale; folded unless the office carries them. */
export interface PlanLeftover { id: string; task_label: string; category: string; season: string; term_end: string | null; status: string; booked_date: string | null }
export function usePlanLeftovers(custno: string | undefined, startDate: string | undefined) {
  return useQuery({
    queryKey: ["plan_leftovers", custno, startDate],
    enabled: !!custno && !!startDate,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PlanLeftover[]> => {
      const { data, error } = await supabase.rpc("pegasus_plan_leftovers", { p_custno: custno, p_start: startDate });
      if (error) throw error;
      return (data ?? []) as PlanLeftover[];
    },
  });
}
/** After a sale: fold the leftovers into the renewal (default) or carry them over. Best-effort — the sale is already saved. */
export async function foldPlanLeftovers(saleId: string, carryOver: boolean): Promise<number> {
  const { data, error } = await supabase.rpc("pegasus_plan_fold_leftovers", { p_sale_id: saleId, p_carry_over: carryOver });
  if (error) throw error;
  return Number(data ?? 0);
}

export function useRenewalStart(custno: string | undefined) {
  return useQuery({
    queryKey: ["plan_renewal_start", custno],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: () => planRenewalStart(custno as string),
  });
}

/**
 * Sell a PLUS plan on an invoice — PLAN-DIAG-ENROLL / PLAN-SALE-INVOICE (2026-10-29).
 *
 * Until today this wrote the plan as SOLD the moment the invoice was finished,
 * paid or not, with no payment record (the June point-of-service enroll). Now
 * it is ONE server write (`pegasus_plan_sale_on_invoice`): the plan is written
 * as ACCEPTED, linked to the invoice — attaching the customer's existing offer
 * when there is one (the homeowner's yes on the repair quote, the office's
 * priced quote) so nothing is sold twice — and PAYMENT activates it: card or
 * check through the ledger, autopay consent, or the ESC posting coming back
 * paid. A zero balance activates at once. Activation lays out the term's
 * tune-ups, counts the check the tech did on this visit as the first one, and
 * folds the old term's leftovers (MEMBERSHIPS-03 4a) — all server-side now, so
 * a payment recorded weeks later still finishes the job.
 *
 * Reused by the office Invoice Builder, the tech app's invoice and the Plan
 * Builder's Sell (which makes the plan its own invoice).
 */
export interface SellPlanInput {
  custno: string;
  locno: string | null;
  term: number;
  baseId: string | null;
  baseName: string | null;
  total: number;
  /** The invoice the plan rides on — payment of it activates the plan. */
  invoiceId: string;
  via: "office" | "tech" | "plan_builder";
  /** The repair quote whose plan offer this fulfils (attaches instead of duplicating). */
  quoteId?: string | null;
  /** A specific offer row to attach (the 360 / Invoice Builder picked it up). */
  saleId?: string | null;
  firstCheck?: { category: string; done: boolean };
  carryOver?: boolean;
  addonIds?: string[];
  itemLines?: unknown[];
  secondSystem?: boolean;
  credit?: { amount: number; invoice: string | null; note: string | null } | null;
  firstCheckSeason?: string | null;
  firstCheckYear?: number | null;
  /** PROPOSAL-EQUIPMENT-02: the units the plan covers — written onto the sale right after it is made
   *  (`pegasus_plan_sale_set_covers`: systems count, per-system snapshot; the invoice line's total kept). */
  covers?: PlanOfferCover[] | null;
}

export function useSellPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: SellPlanInput): Promise<string> => {
      const { data, error } = await supabase.rpc("pegasus_plan_sale_on_invoice", {
        p_custno: v.custno,
        p_locno: v.locno,
        p_invoice_id: v.invoiceId,
        p_term: v.term,
        p_base_id: v.baseId,
        p_total: v.total,
        p_via: v.via,
        p_quote_id: v.quoteId ?? null,
        p_sale_id: v.saleId ?? null,
        p_first_check_category: v.firstCheck?.category ?? null,
        p_first_check_done: !!v.firstCheck?.done,
        p_carry_over: !!v.carryOver,
        p_addon_ids: v.addonIds ?? null,
        p_item_lines: v.itemLines ?? null,
        p_second_system: !!v.secondSystem,
        p_credit_amount: v.credit?.amount ?? 0,
        p_credit_invoice: v.credit?.invoice ?? null,
        p_credit_note: v.credit?.note ?? null,
        p_first_check_season: v.firstCheckSeason ?? null,
        p_first_check_year: v.firstCheckYear ?? null,
      });
      if (error) throw error;
      const saleId = data as string;
      if (saleId && v.covers && v.covers.length) {
        const { error: ce } = await supabase.rpc("pegasus_plan_sale_set_covers", {
          p_sale_id: saleId,
          p_covers: v.covers.map((c) => ({ counter: c.counter, kind: c.kind, label: c.label, loc: c.loc, on: c.on })),
          p_total: v.total,
        });
        if (ce) console.error("plan covers not recorded:", ce);
      }
      return saleId;
    },
    onSuccess: (_id, v) => {
      qc.invalidateQueries({ queryKey: ["plan_sales", v.custno] });
      qc.invalidateQueries({ queryKey: ["pegasus_agreements", v.custno] });
      qc.invalidateQueries({ queryKey: ["plan_offers"] });
      qc.invalidateQueries({ queryKey: ["customer_proposals"] });
      qc.invalidateQueries({ queryKey: ["tuneups_due"] });
      qc.invalidateQueries({ queryKey: ["membership_status"] }); qc.invalidateQueries({ queryKey: ["quote_membership_status"] });
      qc.invalidateQueries({ queryKey: ["membership_card"] });
      qc.invalidateQueries({ queryKey: ["member_discount"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
    },
  });
}

/**
 * PLAN-SALE-INVOICE: a plan sold on its own (the Plan Builder's Sell, a saved
 * quote promoted) gets its own Pegasus invoice — one line, issued, ready for
 * Collect — so a plan can never be active with nothing collected. Returns the
 * invoice id; the caller then writes the plan against it with useSellPlan.
 */
export async function createPlanInvoice(v: {
  custno: string;
  locno: string | null;
  baseName: string | null;
  term: number;
  total: number;
  creditNote?: string | null;
  extras?: string[];
}): Promise<string> {
  const { data: u } = await supabase.auth.getUser();
  const email = u?.user?.email ?? null;
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const years = v.term === 1 ? "1-Year" : `${v.term}-Year`;
  const desc = `${v.baseName ?? "Maintenance Plus"} — ${years} Maintenance Plus Plan${v.extras?.length ? ` · ${v.extras.join(" · ")}` : ""}`;
  const saved = await saveInvoiceRpc({
    dispatchId: null,
    dispatchNo: null,
    expectedVersion: null,
    header: {
      custno: v.custno,
      locno: v.locno,
      type: "service",
      status: "issued",
      completed_date: iso,
      total: Math.round(v.total * 100) / 100,
      internal_notes: ["Maintenance Plus Plan sold on its own — the plan activates when this invoice is paid.", v.creditNote ?? null].filter(Boolean).join("\n"),
    },
    lines: [{ kind: "custom", description: desc, qty: 1, unitPrice: Math.round(v.total * 100) / 100, print: true }],
    via: "office",
    actor: email,
  });
  return saved.id;
}

function invalidatePlanCatalog(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["plan_price_admin"] });
  qc.invalidateQueries({ queryKey: ["plan_catalog"] }); // the builder's read
}

/** Patch one row's editable fields (name, the five term prices, flat, active). */
export function useSavePlanPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      name?: string;
      p1?: number | null; p2?: number | null; p3?: number | null;
      p5?: number | null; p10?: number | null; flat?: number | null;
      active?: boolean; sort?: number;
      tuneups?: TuneupTemplateItem[] | null;
    }) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_plan_price").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidatePlanCatalog(qc),
  });
}

/** New blank row of a kind, dropped at the end of its group. */
export function useAddPlanPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { kind: PlanKind; name: string; afterSort: number }) => {
      // id is a text PK with no DB default — generate one (crypto.randomUUID is
      // available in every browser Pegasus runs in).
      const { error } = await supabase.from("pegasus_plan_price").insert({
        id: crypto.randomUUID(),
        kind: v.kind,
        name: v.name,
        sort: v.afterSort + 10,
        active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidatePlanCatalog(qc),
  });
}

export type RoundMode = "exact" | "dollar" | "five";

/** JS twin of the SQL plan_round — the preview must match what bump writes. */
export function planRound(v: number | null, mode: RoundMode): number | null {
  if (v == null) return null;
  if (mode === "dollar") return Math.round(v);
  if (mode === "five") return Math.round(v / 5) * 5;
  return Math.round(v * 100) / 100;
}

/** Global price increase — raise every price in the chosen kinds by pct%. */
export function useBumpPlanPrices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { pct: number; kinds: PlanKind[]; round: RoundMode }) => {
      const { error } = await supabase.rpc("bump_plan_prices", {
        p_pct: v.pct, p_kinds: v.kinds, p_round: v.round,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidatePlanCatalog(qc),
  });
}

// ── Plan-wide config (trip charge, 2nd-system discount) ─────────────────────
export function usePlanConfig() {
  return useQuery({
    queryKey: ["plan_config_admin"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Record<string, unknown>> => {
      const { data, error } = await supabase.from("pegasus_plan_config").select("key, value");
      if (error) throw error;
      const m: Record<string, unknown> = {};
      for (const c of (data ?? []) as { key: string; value: unknown }[]) m[c.key] = c.value;
      return m;
    },
  });
}

export function useSavePlanConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { key: string; value: unknown }) => {
      const { error } = await supabase
        .from("pegasus_plan_config")
        .upsert({ key: v.key, value: v.value }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plan_config_admin"] });
      qc.invalidateQueries({ queryKey: ["plan_catalog"] });
    },
  });
}

/**
 * Repair-discount fraction for a given plan term, derived from the membership
 * tiers (the highest tier whose min_years ≤ term). The single source the
 * estimate engine and the plan builder both read. Returns 0 if no tier matches.
 */
export function repairPctForTerm(tiers: PlanDiscount[] | undefined, term: number): number {
  if (!tiers || tiers.length === 0) return 0;
  const eligible = tiers
    .filter((t) => Number(t.min_years) <= term)
    .sort((a, b) => Number(b.min_years) - Number(a.min_years));
  if (eligible.length === 0) return 0;
  return Number(eligible[0].repair_pct) / 100;
}
